import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { MdRefresh, MdVerified } from 'react-icons/md';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import StatsGrid from '../../../shared/layout/containers/StatsGrid';
import StatCard from '../../../shared/layout/cards/StatCard';
import MainButton from '../../../shared/layout/buttons/MainButton';
import StandardModal from '../../../shared/layout/modals/StandardModal';
import AdminFilterBar from './components/AdminFilterBar';
import FreshnessBadge from './components/FreshnessBadge';
import {
    approveAdminUserTermination,
    fetchAdminCustomers,
    fetchAdminCustomerStatistics,
} from '../../../services/Http/AdminApi';

const AdminCustomers = () => {
    const theme = useTheme();
    const [rows, setRows] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [terminationFilter, setTerminationFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [sortBy, setSortBy] = useState('created_at');
    const [sortDirection, setSortDirection] = useState('desc');
    const [totalRows, setTotalRows] = useState(0);
    const [pendingTerminationApprovalUser, setPendingTerminationApprovalUser] = useState(null);

    useEffect(() => {
        const timeout = setTimeout(() => setDebouncedSearch(search), 350);
        return () => clearTimeout(timeout);
    }, [search]);

    const loadStats = useCallback(async () => {
        const statsResp = await fetchAdminCustomerStatistics();
        setStats(statsResp?.data || null);
    }, []);

    const loadCustomers = useCallback(async () => {
        setLoading(true);
        try {
            const listResp = await fetchAdminCustomers({
                per_page: perPage,
                page: currentPage,
                search: debouncedSearch,
                sort_by: sortBy,
                sort_dir: sortDirection,
                termination_status: terminationFilter || undefined,
            });
            setRows(listResp?.data || []);
            setTotalRows(listResp?.meta?.total || 0);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to load customers');
        } finally {
            setLoading(false);
        }
    }, [currentPage, debouncedSearch, perPage, sortBy, sortDirection, terminationFilter]);

    useEffect(() => {
        loadCustomers();
    }, [loadCustomers]);

    useEffect(() => {
        loadStats();
    }, [loadStats]);

    const refreshAll = async () => {
        await Promise.all([loadCustomers(), loadStats()]);
    };

    const columns = useMemo(() => [
        { name: 'ID', selector: (r) => r.id, cell: (r) => <Code>{r.id}</Code>, sortable: true, sortField: 'id' },
        { name: 'Name', selector: (r) => `${r.name || ''} ${r.surname || ''}`.trim() || '—', sortable: true, sortField: 'name', wrap: true },
        { name: 'Email', selector: (r) => r.email || '—', sortable: true, sortField: 'email' },
        { name: 'Phone', selector: (r) => r.phone_number || '—' },
        { name: 'City', selector: (r) => r.city || '—', sortable: true, sortField: 'city' },
        {
            name: 'Termination',
            selector: (r) => r.termination_status || 'active',
            cell: (r) => (r.termination_status === 'pending'
                ? <Flag $tone="danger">Pending</Flag>
                : <Flag>Active</Flag>),
            sortable: true,
            sortField: 'termination_status',
        },
        {
            name: 'Default Password',
            selector: (r) => r.has_default_password ? 'yes' : 'no',
            cell: (r) => (r.has_default_password
                ? <Flag $tone="warning">Yes</Flag>
                : <Flag>No</Flag>),
        },
        {
            name: 'Actions',
            cell: (r) => (
                r.termination_status === 'pending'
                    ? (
                        <ActionButton
                            type="button"
                            onClick={() => setPendingTerminationApprovalUser(r)}
                            title="Approve termination"
                        >
                            <MdVerified size={16} />
                            Approve
                        </ActionButton>
                    )
                    : '—'
            ),
            ignoreRowClick: true,
        },
        {
            name: 'Created',
            selector: (r) => r.created_at || '',
            cell: (r) => r.created_at ? new Date(r.created_at).toLocaleDateString() : '—',
            sortable: true,
            sortField: 'created_at',
        },
    ], []);

    const handleSort = (column, direction) => {
        setSortBy(column.sortField || 'created_at');
        setSortDirection(direction);
        setCurrentPage(1);
    };

    return (
        <PageContainer>
            <PageHeader><PageTitle>Customers</PageTitle></PageHeader>
            <PageContent>
                <AdminFilterBar>
                    <FilterRow>
                        <SearchInput
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                            placeholder="Search customer by name/email/id"
                        />
                        <Select
                            value={terminationFilter}
                            onChange={(e) => { setTerminationFilter(e.target.value); setCurrentPage(1); }}
                        >
                            <option value="">All termination statuses</option>
                            <option value="pending">Pending termination</option>
                        </Select>
                        <FreshnessBadge status="success" sourceType="operational" />
                    </FilterRow>
                    <MainButton size="small" onClick={refreshAll}>
                        <MdRefresh style={{ marginRight: 6 }} />
                        Refresh
                    </MainButton>
                </AdminFilterBar>

                <StatsGrid>
                    <StatCard icon="C" title="Total Customers" value={num(stats?.total_customers)} color="transactions" />
                    <StatCard icon="N" title="New (30d)" value={num(stats?.new_customers_30d)} color="info" />
                    <StatCard icon="P" title="Default Password" value={num(stats?.default_password_customers)} color="warning" />
                    <StatCard icon="T" title="Pending Termination" value={num(stats?.pending_termination_customers)} color="danger" />
                </StatsGrid>

                <Section>
                    <DataTable
                        columns={columns}
                        data={rows}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        onChangePage={(page) => setCurrentPage(page)}
                        onChangeRowsPerPage={(newPerPage, page) => {
                            setPerPage(newPerPage);
                            setCurrentPage(page);
                        }}
                        progressPending={loading}
                        persistTableHead
                        highlightOnHover
                        paginationPerPage={perPage}
                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                        noDataComponent="No customers found"
                        sortServer
                        onSort={handleSort}
                        customStyles={getTableStyles(theme)}
                    />
                </Section>
                <StandardModal
                    isOpen={Boolean(pendingTerminationApprovalUser)}
                    onClose={() => setPendingTerminationApprovalUser(null)}
                    title="Approve account termination"
                    footer={(
                        <>
                            <MainButton
                                size="small"
                                type="button"
                                variant="secondary"
                                onClick={() => setPendingTerminationApprovalUser(null)}
                            >
                                Cancel
                            </MainButton>
                            <MainButton
                                size="small"
                                type="button"
                                onClick={async () => {
                                    const user = pendingTerminationApprovalUser;
                                    setPendingTerminationApprovalUser(null);
                                    if (!user) return;
                                    try {
                                        await approveAdminUserTermination(user.id);
                                        toast.success('Termination approved and user deleted.');
                                        await refreshAll();
                                    } catch (error) {
                                        toast.error(error?.response?.data?.message || 'Failed to approve termination');
                                    }
                                }}
                            >
                                Approve termination
                            </MainButton>
                        </>
                    )}
                >
                    <p style={{ margin: 0 }}>
                        Approve termination and delete user{' '}
                        <strong>{pendingTerminationApprovalUser ? `${pendingTerminationApprovalUser.name || ''} ${pendingTerminationApprovalUser.surname || ''}`.trim() : ''}</strong>?
                    </p>
                    <p style={{ margin: '0.5rem 0 0', fontSize: 12, color: theme.colors.textMuted }}>
                        Bill archive email delivery is currently placeholder-only on backend.
                    </p>
                </StandardModal>
            </PageContent>
        </PageContainer>
    );
};

export default AdminCustomers;

const num = (v) => Number(v || 0).toLocaleString();
const FilterRow = styled.div`display:flex;gap: 0.625rem;align-items:center;flex-wrap:wrap;`;
const SearchInput = styled.input`width:320px;border:1px solid ${({theme})=>theme.colors.border};border-radius:10px;padding: 0.5625rem 0.75rem;background:${({theme})=>theme.colors.surface};`;
const Select = styled.select`border:1px solid ${({theme})=>theme.colors.border};border-radius:10px;padding: 0.5625rem 0.75rem;background:${({theme})=>theme.colors.surface};`;
const Section = styled.section`margin-top: 1rem;`;
const Code = styled.code`font-size: 0.75rem;word-break:break-all;`;
const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  padding: 0.375rem 0.625rem;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
`;
const Flag = styled.span`
  display:inline-flex;align-items:center;border-radius:999px;padding: 0.1875rem 0.5rem;font-size: 0.75rem;font-weight:700;
  background:${({theme,$tone}) => $tone === 'warning'
    ? theme.colors.warningBg
    : $tone === 'danger'
      ? theme.colors.errorBg || '#fee2e2'
      : theme.colors.surfaceMuted};
  color:${({theme,$tone}) => $tone === 'warning'
    ? theme.colors.warning
    : $tone === 'danger'
      ? theme.colors.error || '#b91c1c'
      : theme.colors.textMuted};
`;

const getTableStyles = (theme) => ({
    table: { style: { border: `1px solid ${theme.colors.border}`, borderRadius: 12, overflow: 'hidden' } },
    headRow: { style: { backgroundColor: theme.colors.tableHeaderBg || '#f9fafb', minHeight: 46 } },
    headCells: { style: { fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: theme.colors.textMuted } },
    rows: { style: { minHeight: 50, '&:hover': { backgroundColor: theme.colors.tableRowHoverBg || '#f8fafc' } } },
    cells: { style: { fontSize: 14, color: theme.colors.textPrimary } },
});
